import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const ManageModulesPage = ({ user, onLogout }) => {
  const [modules, setModules] = useState([]);
  const [editingModule, setEditingModule] = useState(null);
  const [showNewModule, setShowNewModule] = useState(false);
  const [formData, setFormData] = useState({
    module_number: '',
    title: '',
    description: '',
    duration_minutes: '',
    objectives: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await api.get('/modules');
      setModules(response.data);
    } catch (err) {
      console.error('Error fetching modules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (module) => {
    setEditingModule(module.id);
    setFormData({
      module_number: module.module_number,
      title: module.title,
      description: module.description,
      duration_minutes: module.duration_minutes,
      objectives: module.objectives.join('\n')
    });
    setShowNewModule(false);
  };

  const handleCreate = () => {
    setShowNewModule(true);
    setEditingModule(null);
    setFormData({
      module_number: modules.length + 1,
      title: '',
      description: '',
      duration_minutes: '',
      objectives: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      module_number: parseInt(formData.module_number),
      duration_minutes: parseInt(formData.duration_minutes),
      objectives: formData.objectives.split('\n').filter(o => o.trim())
    };

    try {
      if (editingModule) {
        await api.put(`/modules/${editingModule}`, data);
        alert('Module updated successfully!');
      } else {
        await api.post('/modules', data);
        alert('Module created successfully!');
      }
      
      setEditingModule(null);
      setShowNewModule(false);
      fetchModules();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save module');
    }
  };

  const handleDelete = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module? This will also delete associated tests and progress.')) {
      return;
    }

    try {
      await api.delete(`/modules/${moduleId}`);
      alert('Module deleted successfully!');
      fetchModules();
    } catch (err) {
      alert('Failed to delete module');
    }
  };

  const handleCancel = () => {
    setEditingModule(null);
    setShowNewModule(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard" data-testid="manage-modules-page">
      <div className="dashboard-header">
        <h1>Manage Training Modules</h1>
        <div className="user-info">
          <Link to="/dashboard" className="btn btn-secondary" data-testid="back-to-dashboard-btn">Back to Dashboard</Link>
          <button onClick={onLogout} className="btn-logout" data-testid="logout-btn">Logout</button>
        </div>
      </div>
      
      <div className="dashboard-content">
        <div style={{ marginBottom: '30px' }}>
          <button onClick={handleCreate} className="btn btn-primary" data-testid="create-module-btn">
            + Create New Module
          </button>
        </div>
        
        {(showNewModule || editingModule) && (
          <div className="modules-section" style={{ marginBottom: '30px' }} data-testid="module-form">
            <h3>{editingModule ? 'Edit Module' : 'Create New Module'}</h3>
            <form onSubmit={handleSubmit} style={{ marginTop: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label>Module Number</label>
                  <input
                    type="number"
                    value={formData.module_number}
                    onChange={(e) => setFormData({ ...formData, module_number: e.target.value })}
                    required
                    data-testid="module-number-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>Duration (minutes)</label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    required
                    data-testid="duration-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Module Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="title-input"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="3"
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0' }}
                  data-testid="description-input"
                />
              </div>
              
              <div className="form-group">
                <label>Learning Objectives (one per line)</label>
                <textarea
                  value={formData.objectives}
                  onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                  required
                  rows="5"
                  placeholder="Enter each learning objective on a new line..."
                  style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0' }}
                  data-testid="objectives-input"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '15px' }}>
                <button type="submit" className="btn-full" data-testid="save-module-btn">
                  {editingModule ? 'Update Module' : 'Create Module'}
                </button>
                <button type="button" onClick={handleCancel} className="btn btn-secondary" style={{ flex: 1 }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
        
        <div className="modules-section">
          <h2>All Modules ({modules.length})</h2>
          <div className="modules-grid">
            {modules.map(module => (
              <div key={module.id} className="module-card" data-testid={`module-${module.module_number}`}>
                <div className="module-number">MODULE {module.module_number}</div>
                <h3>{module.title}</h3>
                <p>{module.description}</p>
                <div className="module-duration">Duration: {module.duration_minutes} minutes</div>
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <button 
                    onClick={() => handleEdit(module)} 
                    className="btn-module"
                    style={{ background: '#3b82f6' }}
                    data-testid={`edit-module-${module.module_number}`}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(module.id)} 
                    className="btn-module"
                    style={{ background: '#dc2626' }}
                    data-testid={`delete-module-${module.module_number}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageModulesPage;
